import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTenantStore } from '../stores/tenant.store';

// ─── Domain-role separation (landing vs app/backoffice custom domains) ──────
// A seminary's LANDING custom domain (e.g. sebex.cl) is PUBLIC marketing ONLY —
// it serves no authenticated routes. The full app (admin dashboard AND the
// student /portal-v2, with login) lives on the APP/backoffice domain
// (e.g. selfservice.sebex.cl). So on a landing domain, ANY non-public route is
// redirected path-preserving to the same resource on the app domain (never a
// generic login that drops the deep-link). No app domain configured → /no-access.
//
// This is an ALLOWLIST of public routes (everything else redirects), the inverse
// of the old "redirect dashboard segments" approach: /portal-v2 and /login are
// now redirected too, so the landing domain never holds a session. The hard
// security boundary is the tenant scoping in auth.context; this is UX separation.
// `programas`: la página pública de cada programa en el dominio propio (/programas/:code).
const PUBLIC_SEGMENTS = new Set(['s', 'apply', 'auth', 'registro', 'programas']);

/** True when `pathname` may stay on a landing domain (public marketing / apply flows). */
export function isPublicLandingPath(pathname: string): boolean {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  if (first === '' || first === 'no-access') return true; // root + no-access notice
  return PUBLIC_SEGMENTS.has(first);
}

/** Path-preserving URL to the same resource on the seminary's backoffice domain. */
export function backofficeResourceUrl(
  backofficeDomain: string,
  pathname: string,
  search: string,
): string {
  return `https://${backofficeDomain}${pathname}${search}`;
}

export function DomainRoleEnforcer(): null {
  const { currentDomain } = useTenantStore();
  const location = useLocation();
  const navigate = useNavigate();
  const redirecting = useRef(false);

  useEffect(() => {
    if (redirecting.current) return;
    if (currentDomain?.type !== 'landing') return;
    if (isPublicLandingPath(location.pathname)) return; // public route → stays here

    redirecting.current = true;
    void (async () => {
      let backoffice: string | null = null;
      try {
        const snap = await getDoc(doc(db, 'tenants', currentDomain.tenantId));
        const data = snap.data() as
          | { config?: { customDomains?: { backoffice?: string } } }
          | undefined;
        backoffice = data?.config?.customDomains?.backoffice ?? null;
      } catch {
        /* fall through to the no-access notice */
      }
      if (backoffice) {
        // Cross-domain, path-preserving: the same resource on the backoffice domain.
        window.location.href = backofficeResourceUrl(
          backoffice,
          location.pathname,
          location.search,
        );
      } else {
        // No app/backoffice domain configured → the app isn't served here.
        redirecting.current = false;
        navigate('/no-access', { replace: true });
      }
    })();
  }, [currentDomain, location.pathname, location.search, navigate]);

  return null;
}
